import { Routes } from '@angular/router';

import { PERMISSIONS } from '@23degrees/shared-code/config/permissions';

import { AuthGuard } from '@23degrees/shared/guards/auth.guard';
import { CreateGuard } from '@23degrees/shared/guards/create-guard';
import { FoldersGuard } from '@23degrees/shared/guards/folders.guard';
import { CanLeaveGuard } from '@23degrees/shared/guards/leave-export.guard';
import { PermissionGuard } from '@23degrees/shared/services/permission-guard/permission.guard';

import { provideFrontendConfig } from '@23degrees/frontend/config/config.provider';
import { provideIcons } from '@23degrees/frontend/icons';
import { provideMaterialConfig } from '@23degrees/frontend/material';
import { provideLoader } from '@23degrees/layout/services/loading';
import { provideMediaWatcher } from '@23degrees/layout/services/media-watcher';
import { providePlatformInfo } from '@23degrees/shared/services/platform';
import { isDevMode } from '@angular/core';
import { mainConfig } from './atlas.config';
import { contentMatcher, foldersPageMatcher } from './atlas.matchers';
import { PageLoadingComponent } from './page-loading.component';

// import { provideGoogle } from '@23degrees/frontend/google';
import { provideSentry } from '@23degrees/frontend/sentry';
import { provideLanguage } from '@23degrees/shared/services/language';
import { StandAloneRoutes } from './atlas.routing-standalone';
// import { provideConsentBannerConfig } from '@23degrees/packages/consent-banner/provider';
import { provideAnalytics } from '@23degrees/frontend/analytics/providers';

const AtlasRoutes: Routes = [
  {
    path: '',
    providers: [
      provideLanguage(),
      provideSentry(!isDevMode()),
      provideIcons(),
      provideLoader(),
      providePlatformInfo(),
      provideMaterialConfig({ apperance: 'fill' }),
      provideMediaWatcher(),
      provideFrontendConfig(mainConfig),
      provideAnalytics(!isDevMode())
    ],
    children: [
      // GLOBAL ROUTES: NOT LAYOUT
      ...StandAloneRoutes.map((route) => ({
        ...route,
        data: {
          ...route.data,
          revalidate: 0,
        }
      })),
      {
        path: '',
        loadComponent: () => import('@23degrees/layout/layout.component'),
        data: {
          layout: 'dense',
        },
        children: [
          {
            path: '',
            loadChildren: () => import('./pages/home-page/home-page.routes'),
            canActivate: [AuthGuard],
            data: {
              title: 'Create, find and reuse interactive data visualizations - 23°',
              showTryButton: true,
              revalidate: 0,
            },
          },
          {
            path: 'dashboard',
            loadChildren: () => import('./pages/user/user-landing-page/user-landing-page.routes'),
            canActivate: [AuthGuard],
            data: {
              title: 'Create, find and reuse interactive data visualizations - 23°',
              showTryButton: true,
            },
          },

          {
            path: 'create',
            loadChildren: () => import('./pages/create-page/create-page.routes'),
            canActivate: [CreateGuard],
            // canActivate: [AuthGuard],
            data: {
              title: 'Create it - 23°',
              // permission: PERMISSIONS.USERMANAGEMENT_VIEW,
              showTryButton: true,
              preload: true,
            },
          },
          {
            path: 'user-profile',
            loadChildren: () => import('./pages/user/user-profile-page/user-profile-page.routes'),
            canActivate: [AuthGuard],
            data: {
              title: 'User Profile',
            },
          },
          {
            path: 'user-profile/:slug',
            loadChildren: () => import('./pages/user/user-profile-page/user-profile-page.routes'),
            canActivate: [AuthGuard],
            data: {
              title: 'User Profile',
            },
          },
          {
            path: 'search/:query',
            loadChildren: () => import('./pages/search-page/search-page.routes'),
            canActivate: [AuthGuard],
          },
          {
            path: 'typesense',
            loadChildren: () => import('./pages/typesense-page/typesense-page.routes'),
            canActivate: [AuthGuard],
          },
          {
            path: 'editor/:slug',
            redirectTo: 'view/:slug',
            pathMatch: 'full',
          },
          {
            path: 'export/:slug',
            redirectTo: 'view/:slug/',
            pathMatch: 'prefix',
          },
          {
            // path view, viewSub, edit, editSub, export
            loadChildren: () => import('./pages/content-page/content-page.routes'),
            canActivate: [AuthGuard],
            canDeactivate: [],
            matcher: contentMatcher,
            data: {
              revalidate: 100,
              preload: true,
            },
          },

          // ALL REDIRECTS TO NEW FOLDER PAGE
          {
            path: 'collections/:slug',
            redirectTo: 'folders/:slug',
            pathMatch: 'full',
          },
          {
            path: 'collections',
            redirectTo: 'folders',
            pathMatch: 'full',
          },
          {
            path: 'shared-with-me',
            redirectTo: 'shared',
            pathMatch: 'full',
          },
          {
            path: 'shared-with-me/:slug',
            redirectTo: 'shared/:slug',
            pathMatch: 'full',
          },
          {
            path: 'garbage',
            redirectTo: 'trash',
            pathMatch: 'full',
          },
          {
            matcher: foldersPageMatcher,
            loadChildren: () => import('./pages/folders-page/folders-page.routes'),
            canActivate: [AuthGuard, FoldersGuard],
            data: {
              icon: 'folder-open',
              title: 'Folders',
              translationString: 'page.collections.routeTitle',
              serviceMethod: 'getCollections',
            },
          },

          {
            path: 'explore',
            loadChildren: () => import('./pages/explore-page/explore-page.routes'),
            canActivate: [AuthGuard],
            data: {
              title: 'Explore',
            },
          },

          {
            path: 'stream/covid-19',
            redirectTo: 'indicator/covid',
            pathMatch: 'full',
          },

          {
            path: 'indicator',
            loadChildren: () => import('./pages/indicator-page/indicator-page.routes'),
            canActivate: [AuthGuard],
            data: {
              // showTryButton: true,
            },
          },
          {
            path: 'stream/:type',
            loadChildren: () => import('./pages/stream-page/stream-page.routes'),
            canActivate: [AuthGuard],
            data: {
              // showTryButton: true,
            },
          },
          {
            path: 'datacore',
            loadChildren: () => import('./pages/datacore-page/datacore-page.routes'),
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              permission: PERMISSIONS.DATACORE_VIEW,
            },
          },
          {
            path: 'crowd-control',
            loadChildren: () => import('@23degrees/admin/pages/permission-admin-page/permission-admin-page.routes'),
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              title: 'Permission Admin',
              permission: PERMISSIONS.USERMANAGEMENT_VIEW,
            },
          },
          {
            path: 'admin',
            loadChildren: () => import('@23degrees/apps/admin/pages/crowd-control/admin-page.routes'),
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              title: 'Permission Admin',
              permission: PERMISSIONS.USERMANAGEMENT_VIEW,
            },
          },
          {
            path: 'language-control',
            loadChildren: () => import('@23degrees/admin/pages/language-admin-page/language-admin-page.routes'),
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              title: 'Language Control',
              permission: PERMISSIONS.LANGUAGE_EDIT,
            },
          },
          {
            path: 'category-management',
            loadChildren: () => import('@23degrees/apps/admin/pages/category-admin-page/category-admin-page.routes'),
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              title: 'Category Management',
              permission: PERMISSIONS.TAGMANAGEMENT_VIEW,
            },
          },
          {
            path: 'tag-management',
            loadChildren: () => import('@23degrees/admin/pages/tag-admin-page/tag-admin-page.routes'),
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              title: 'Tag Management',
              permission: PERMISSIONS.TAGMANAGEMENT_VIEW,
            },
          },
          {
            path: 'topic-management',
            loadChildren: () => import('@23degrees/admin/pages/topic-admin-page/topic-admin-page.routes'),
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              title: 'Topic Management',
              permission: PERMISSIONS.TAGMANAGEMENT_VIEW,
            },
          },
          {
            path: 'tutorial-management',
            loadChildren: () => import('@23degrees/admin/pages/tutorial-admin-page/tutorial-admin-page.routes'),
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              title: 'Tutorial Management',
              permission: PERMISSIONS.TUTORIAL_PAGE_ADMIN,
            },
          },
          {
            path: 'contact-us',
            loadChildren: () => import('./pages/contact-form-page/contact-form-page.routes'),
            data: {
              title: 'Imprint',
              revalidate: 0
            },
          },
          {
            path: 'tutorials',
            redirectTo: 'academy',
            pathMatch: 'full',
          },
          {
            path: 'tutorials/:category',
            redirectTo: 'academy',
            pathMatch: 'full',
          },
          {
            path: 'tutorials/:category/:slug',
            redirectTo: 'academy',
            pathMatch: 'full',
          },
          {
            path: 'academy',
            loadChildren: () => import('./pages/academy/academy.routes'),
            canActivate: [AuthGuard],
            // canActivate: [AuthGuard, PermissionGuard],
            data: {
              title: 'Academy',
              showTryButton: true,
            },
          },
          {
            path: 'dataset',
            loadChildren: () => import('./pages/dataset-page/dataset-page.routes'),
            canActivate: [AuthGuard],
            // canActivate: [AuthGuard, PermissionGuard],
            data: {
              title: 'Dataset',
              // permission: PERMISSIONS.TUTORIAL_PAGE_VIEW,
            },
          },
          {
            path: 'media',
            loadChildren: () => import('./pages/media-page/media-page.routes'),
            canActivate: [AuthGuard],
            data: {
              title: 'Media',
              showTryButton: true,
            },
          },
          {
            path: 'topics',
            loadChildren: () => import('./pages/topic-page/topic-page.routes'),
            canActivate: [AuthGuard],
            data: {
              title: 'Topics',
              showTryButton: true,
            },
          },
          {
            path: 'user-settings',
            loadChildren: () => import('./pages/user/user-settings-page/user-settings-page.routes'),
            canActivate: [AuthGuard],
            data: {
              title: 'User Settings',
            },
          },
          {
            path: 'user',
            loadChildren: () => import('./pages/user/user-page.routes'),
            canActivate: [AuthGuard],
          },
          {
            path: 'customer-stories',
            loadChildren: () => import('./pages/customer-stories/customer-stories.routes'),
            canActivate: [AuthGuard],
            data: {
              showTryButton: true,
              title: 'Customer stories',
            },
          },
          {
            path: 'about',
            loadChildren: () => import('./pages/about-page/about-page.routes'),
            canActivate: [AuthGuard],
            data: {
              title: 'About',
            },
          },
          {
            path: 'contact',
            loadChildren: () => import('./pages/contact-page/contact-page.routes'),
            canActivate: [AuthGuard],
            data: {
              title: 'Contact',
            },
          },
          {
            path: 'jobs',
            loadChildren: () => import('./pages/contact-page/contact-page.routes'),
            canActivate: [AuthGuard],
            data: {
              title: 'Contact',
            },
          },
          {
            path: 'pricing',
            loadChildren: () => import('./pages/pricing-page/pricing-page.routes'),
            canActivate: [AuthGuard],
            data: {
              title: 'Pricing',
            },
          },
          {
            path: 'user-products',
            loadChildren: () => import('./pages/user/user-products-page/user-products-page.routes'),
            canActivate: [AuthGuard],
            data: {
              title: 'Your subscriptions',
            },
          },

          {
            path: 'solutions',
            loadChildren: () => import('./pages/solutions-page/solutions-page.routes'),
          },
          {
            path: 'imprint',
            loadChildren: () => import('./pages/imprint-page/imprint-page.routes'),
            data: {
              title: 'Imprint',
            },
          },
          {
            path: 'privacy-policy',
            loadChildren: () => import('./pages/privacy-page/privacy-page.routes'),
            data: {
              title: 'Privacy policy',
            },
          },
          {
            path: 'privacy',
            loadChildren: () => import('./pages/privacy-page/privacy-page.routes'),
            data: {
              title: 'Privacy policy',
            },
          },
          {
            path: 'terms',
            loadChildren: () => import('./pages/terms-page/terms-page.routes'),
            data: {
              title: 'Terms',
            },
          },
          {
            path: 'playground',
            loadChildren: () => import('./pages/playground-page/playground-page.routes'),
            canActivate: [AuthGuard, PermissionGuard],
            data: {
              title: 'Playground',
              permission: PERMISSIONS.USERMANAGEMENT_VIEW,
            },
          },

          {
            path: 'loading',
            component: PageLoadingComponent,
            data: {
              title: 'Loading...',
            },
          },
        ],
      },
    ],
  },
];

export default AtlasRoutes;
